"use client";
// HomeSection — display_type → bileşen yönlendirmesi + KATMAN 2'nin görünen ucu.
//
// Render edilecek section'ı usePersonalizedSection belirliyor: kimlikli sürüm
// geldiyse o, gelmediyse (misafir / manual section / istek hata verdi)
// HomeSectionLoader'ın ISR cache'inden gelen anonim sürüm. İki katmanın da aynı
// yönlendirme mantığını kullanması için bu dosya tek yer.
//
// Kişiselleşebilir section'lar PersonalizationGate ile sarılıyor: ISR'ın anonim
// içeriği ve skeleton birlikte basılıyor, hangisinin görüneceğine CSS karar
// veriyor. Bu kararın React'te olmaması kasıtlı — gerekçesi
// usePersonalizedSection ve authGateScript.ts'te.
import {
  Section,
  isCollectionSection,
  isSliderSection,
  isStorySection,
} from "@/app/lib/features/home/home.types";
import usePersonalizedSection from "@/app/lib/features/home/usePersonalizedSection";
import { HomeAnySection } from "@/app/lib/features/home/home.state";
import { shouldSkipSection } from "@/app/constants/sectionConfig";
import SectionRenderer from "./SectionRenderer";
import StoriesSection from "./StoriesSection";
import BannerSection from "./BannerSection";
import BrandsSection from "./BrandsSection";
import CategoriesDiscoverSection from "./CategoriesDiscoverSection";
import CategoryLinksSection from "./CategoryLinksSection";
import CategoriesSection from "./CategoriesSection";
import CollectionsSection from "./CollectionsSection";
import PopularCategoriesSection from "./PopularCategoriesSection";
import PersonalizationGate from "./PersonalizationGate";
import ProductSectionSkeleton from "../../elements/skeletons/ProductSectionSkeleton";
import CollectionsSkeleton from "../../elements/skeletons/CollectionsSkeleton";

interface HomeSectionProps {
  /** Section anahtarı — API'de key ve slug aynı değer. */
  slug: string;
  /** KATMAN 1'in anonim çıktısı. Kimlikli sürüm yokken bu render edilir. */
  ssrSection: HomeAnySection;
  isFirst?: boolean;
}

const hasItems = (section: HomeAnySection) =>
  "items" in section && !!section.items?.length;

const hasBackgroundColor = (section: Section) =>
  section.items?.some((item) => item.background_color);

const HomeSection = ({ slug, ssrSection, isFirst }: HomeSectionProps) => {
  const { section, hasGate, gateState } = usePersonalizedSection(
    slug,
    ssrSection,
  );

  if (isStorySection(section)) {
    if (!hasItems(section)) return null;
    return <StoriesSection stories={section.items} sectionId={section.id} />;
  }

  // Slider = ana sayfanın üst banner alanı. Kişiselleşmiyor (content_source
  // "slider"), o yüzden PersonalizationGate'e gerek yok.
  if (isSliderSection(section)) {
    if (!hasItems(section)) return null;
    return <BannerSection items={section.items} />;
  }

  if (isCollectionSection(section)) {
    return (
      <PersonalizationGate
        enabled={hasGate}
        state={gateState}
        skeleton={<CollectionsSkeleton />}
      >
        {hasItems(section) ? <CollectionsSection section={section} /> : null}
      </PersonalizationGate>
    );
  }

  const s = section as Section;

  if (s.display_type === "hero") {
    if (!hasItems(s)) return null;
    return <CategoriesDiscoverSection section={s} />;
  }

  if (s.display_type === "categories") {
    if (!hasItems(s)) return null;
    return <CategoryLinksSection categories={s.items!} sectionId={s.id} />;
  }

  if (s.display_type === "brands") {
    if (!hasItems(s)) return null;
    return (
      <BrandsSection
        brands={s.items!}
        title={s.title}
        icon={s.style?.icon}
        sectionId={s.id}
      />
    );
  }

  if (s.display_type === "instagram") {
    if (!hasItems(s)) return null;
    return (
      <CategoriesSection
        categories={s.items!}
        title={s.title}
        description={s.description}
        sectionId={s.id}
      />
    );
  }

  if (
    s.display_type === "grid" &&
    s.content_source === "manual" &&
    hasItems(s) &&
    hasBackgroundColor(s)
  ) {
    return (
      <PopularCategoriesSection
        categories={s.items!}
        title={s.title}
        sectionId={s.id}
      />
    );
  }

  if (shouldSkipSection(s)) return null;

  // Buraya yalnızca ürün section'ları ulaşır: carousel, auto-grid, tabs.
  // ProductSectionSkeleton, page.tsx'teki Suspense fallback'iyle aynı bileşen —
  // yükseklik eşleştiği için skeleton ↔ içerik geçişinde layout shift olmuyor.
  return (
    <PersonalizationGate
      enabled={hasGate}
      state={gateState}
      skeleton={<ProductSectionSkeleton />}
    >
      <SectionRenderer section={s} isFirst={isFirst} />
    </PersonalizationGate>
  );
};

export default HomeSection;
